/**
 * ADS.1 — Meta Pixel + CAPI dual-fire for accounts.riverr.app / admin.
 * Keep event names in sync with riverr-enterprise-nodejs/lib/meta/conversionsApi.js
 */

import { nodeServer } from "../api/ajax";

const STANDARD_EVENTS = new Set([
  "ViewContent",
  "PageView",
  "CompleteRegistration",
  "StartTrial",
  "Subscribe",
  "Lead",
  "Purchase",
]);

export const RIVERR_ADS_EVENTS = {
  VIEW_CONTENT: "ViewContent",
  COMPLETE_REGISTRATION: "CompleteRegistration",
  START_TRIAL: "StartTrial",
  SUBSCRIBE: "Subscribe",
  STORE_CREATED: "store_created",
};

const SIGNUP_SOURCE_KEY = "riverr_ads_signup_source";

const capiUrl = () => `${nodeServer}/public/meta/events`;

export const createMetaEventId = (eventName = "evt") => {
  const stamp = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  const slug = String(eventName || "evt")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .slice(0, 24);
  return `${slug}_${stamp}_${rand}`;
};

const readCookie = (name) => {
  if (typeof document === "undefined") return "";
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  if (!match) return "";
  return decodeURIComponent(match.slice(name.length + 1));
};

export const getMetaClickIds = () => {
  if (typeof window === "undefined") {
    return { fbc: "", fbp: "", fbclid: "" };
  }
  let fbclid = "";
  try {
    fbclid = new URLSearchParams(window.location.search).get("fbclid") || "";
    if (fbclid) {
      window.sessionStorage.setItem("riverr_ads_fbclid", fbclid);
    } else {
      fbclid = window.sessionStorage.getItem("riverr_ads_fbclid") || "";
    }
  } catch {
    fbclid = "";
  }
  const fbp = readCookie("_fbp");
  let fbc = readCookie("_fbc");
  if (!fbc && fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
  return { fbc, fbp, fbclid };
};

const inferSignupSourceFromPath = (path = "") => {
  const p = String(path || "").toLowerCase();
  if (p.includes("embroidery")) return "embroidery_tool";
  if (p.includes("sticker")) return "sticker_ad";
  if (p.includes("gang-sheet") || p.includes("gangsheet")) return "gangsheet_ad";
  if (p.includes("designer")) return "designer_ad";
  if (p.includes("chaos")) return "chaos_ad";
  return "";
};

export const captureSignupSource = () => {
  if (typeof window === "undefined") return "";
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl =
      params.get("signup_source") ||
      params.get("src") ||
      params.get("utm_campaign") ||
      "";
    if (fromUrl) {
      window.sessionStorage.setItem(SIGNUP_SOURCE_KEY, fromUrl);
      return fromUrl;
    }
    const stored = window.sessionStorage.getItem(SIGNUP_SOURCE_KEY) || "";
    if (stored) return stored;
    let referrerPath = "";
    try {
      referrerPath = new URL(document.referrer).pathname;
    } catch {
      referrerPath = "";
    }
    const inferred =
      inferSignupSourceFromPath(window.location.pathname) ||
      inferSignupSourceFromPath(referrerPath);
    if (inferred) window.sessionStorage.setItem(SIGNUP_SOURCE_KEY, inferred);
    return inferred;
  } catch {
    return "";
  }
};

export const withRiverrAdsContext = (payload = {}) => {
  const ids = getMetaClickIds();
  const eventId =
    payload.event_id ||
    payload.eventId ||
    createMetaEventId("CompleteRegistration");
  return {
    ...payload,
    event_id: eventId,
    fbc: ids.fbc || undefined,
    fbp: ids.fbp || undefined,
    fbclid: ids.fbclid || undefined,
    event_source_url:
      payload.event_source_url ||
      (typeof window !== "undefined" ? window.location.href : undefined),
    signup_source: payload.signup_source || captureSignupSource() || undefined,
  };
};

const getFbq = () => {
  if (typeof window === "undefined") return null;
  return typeof window.fbq === "function" ? window.fbq : null;
};

const sendCapi = (payload) => {
  if (typeof window === "undefined") return;
  fetch(capiUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
    cache: "no-store",
  }).catch(() => {});
};

export const trackRiverrAdsEvent = (eventName, options = {}) => {
  if (typeof window === "undefined" || !eventName) return "";
  const eventId = options.eventId || createMetaEventId(eventName);
  const ids = getMetaClickIds();
  const signupSource = captureSignupSource();
  const customData = {
    ...(signupSource ? { signup_source: signupSource } : {}),
    ...(options.customData || {}),
  };

  if (!options.skipPixel) {
    const fbq = getFbq();
    if (fbq) {
      try {
        const method = STANDARD_EVENTS.has(eventName) ? "track" : "trackCustom";
        fbq(method, eventName, customData, { eventID: eventId });
      } catch {
        // ignore
      }
    }
  }

  if (!options.skipCapi) {
    sendCapi({
      event_name: eventName,
      event_id: eventId,
      event_source_url: window.location.href,
      email: options.email || undefined,
      fbc: ids.fbc || undefined,
      fbp: ids.fbp || undefined,
      fbclid: ids.fbclid || undefined,
      custom_data: customData,
      user_agent: navigator?.userAgent || undefined,
    });
  }

  return eventId;
};

export const shouldLoadRiverrAdsPixel = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return (
    host === "accounts.riverr.app" ||
    host === "app.riverr.app" ||
    host.endsWith(".myriverr.com")
  );
};

export const getRiverrAdsPixelId = () =>
  String(process.env.REACT_APP_META_PIXEL_ID || "").trim();
